/**
 * StreetView — Google Maps Embed iframe in Street View mode.
 *
 * Uses the **free** Maps Embed API (not the paid Static API). As long as we
 * ship an iframe, Google doesn't bill — even though Street View imagery,
 * UI chrome and navigation are fully interactive. The paid Static API only
 * matters when we eventually generate static JPEGs for PDF exports.
 *
 * Key setup:
 *  - `VITE_GOOGLE_MAPS_KEY` is restricted by HTTP referrer in GCP Console,
 *    so it's safe to include in the browser bundle.
 *  - Required API activation in GCP: "Maps Embed API".
 *
 * If the key is missing at build time (e.g. preview deploy forgot to set the
 * secret), we show a graceful placeholder instead of a broken iframe.
 */

import { useEffect, useState } from "react";
import { Pill } from "./ui";
import { Ic } from "./icons";

const EMBED_URL = "https://www.google.com/maps/embed/v1/streetview";

// Optional: Street View Metadata endpoint — free, used to check coverage
// *before* showing the iframe. Avoids a jarring "Sorry, we have no imagery
// here" message in residential / rural areas where Google hasn't driven.
const METADATA_URL =
  "https://maps.googleapis.com/maps/api/streetview/metadata";

export function StreetView({
  lat,
  lng,
  heading,
  pitch = 0,
  fov = 90,
  className = "",
}: {
  lat: number;
  lng: number;
  /** Camera heading in degrees (0 = north, 90 = east, etc.). Omit to use
   * Google's default heading for the picked pano. */
  heading?: number;
  /** Camera pitch in degrees (-90 = straight down, 0 = horizon, 90 = up). */
  pitch?: number;
  /** Horizontal field of view in degrees. 90 is the default; lower = zoomed in. */
  fov?: number;
  className?: string;
}) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined;
  const [coverage, setCoverage] = useState<"checking" | "ok" | "none" | "skip">(
    "checking",
  );

  // Check coverage via the (free, unlimited) metadata endpoint. Status values:
  //  - OK              → coverage exists, show the iframe
  //  - ZERO_RESULTS    → no pano within the search radius, show placeholder
  //  - REQUEST_DENIED  → Street View Static API isn't activated on the GCP
  //                      project yet (we're running with Maps Embed only).
  //                      Fall through to the iframe — Google will render its
  //                      own "no imagery" state when relevant.
  //  - OVER_QUERY_LIMIT or network error → optimistic fallback to iframe.
  useEffect(() => {
    if (!apiKey) {
      setCoverage("skip");
      return;
    }
    let cancelled = false;
    const qs = new URLSearchParams({
      location: `${lat},${lng}`,
      source: "outdoor",
      radius: "50",
      key: apiKey,
    });
    fetch(`${METADATA_URL}?${qs.toString()}`)
      .then((r) => r.json())
      .then((body: { status?: string }) => {
        if (cancelled) return;
        if (body.status === "OK") setCoverage("ok");
        else if (body.status === "ZERO_RESULTS") setCoverage("none");
        else setCoverage("ok"); // denied / over-limit → trust iframe path
      })
      .catch(() => {
        if (!cancelled) setCoverage("ok");
      });
    return () => {
      cancelled = true;
    };
  }, [apiKey, lat, lng]);

  if (!apiKey) {
    return (
      <PlaceholderCard
        className={className}
        title="Clé Google Maps manquante"
        body="Configurez VITE_GOOGLE_MAPS_KEY pour activer la vue rue."
      />
    );
  }

  if (coverage === "none") {
    return (
      <PlaceholderCard
        className={className}
        title="Vue rue indisponible"
        body="Google Street View n'a pas couvert cette adresse. Les rues privées, chemins et lieux-dits en sont souvent dépourvus."
      />
    );
  }

  const params = new URLSearchParams({
    key: apiKey,
    location: `${lat},${lng}`,
    pitch: String(pitch),
    fov: String(fov),
  });
  if (heading !== undefined) params.set("heading", String(heading));

  return (
    <div
      className={`relative rounded-xl2 overflow-hidden hairline bg-ink-900 ${className}`}
    >
      <iframe
        title="Vue rue (Google Street View)"
        src={`${EMBED_URL}?${params.toString()}`}
        className="absolute inset-0 w-full h-full border-0"
        loading="lazy"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
      />
      <div className="absolute top-3 left-3 pointer-events-none">
        <Pill tone="soft" icon={<Ic.Pin size={12} />}>
          Vue rue — Google
        </Pill>
      </div>
    </div>
  );
}

function PlaceholderCard({
  className,
  title,
  body,
}: {
  className: string;
  title: string;
  body: string;
}) {
  return (
    <div
      className={`relative rounded-xl2 overflow-hidden hairline bg-ink-50 flex items-center justify-center ${className}`}
    >
      <div className="max-w-[280px] text-center px-6">
        <div className="text-[13px] font-semibold text-ink-800">{title}</div>
        <div className="mt-1 text-[11.5px] text-ink-500 leading-relaxed">
          {body}
        </div>
      </div>
    </div>
  );
}
